import { usePostHog } from "posthog-js/react";
import { useState, useRef, useCallback, useEffect } from "react";

interface CardRect {
  id: string;
  rect: {
    left: number;
    top: number;
    right: number;
    bottom: number;
  };
}

interface SelectionRect {
  startX: number;
  startY: number;
  currentX: number;
  currentY: number;
}

export interface DragSelectState {
  selectedIds: string[];
  isDraggingCards: boolean;
  dropTargetId: string | undefined;
  lastSelectedId: string | undefined;
  isModifierKeyPressed: boolean;
  selectionRect: SelectionRect | undefined;
}

export function useDragSelect<T extends HTMLElement>() {
  const posthog = usePostHog();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isDraggingCards, setIsDraggingCards] = useState(false);
  const [dropTargetId, setDropTargetId] = useState<string | undefined>();
  const [lastSelectedId, setLastSelectedId] = useState<string | undefined>();
  const [isModifierKeyPressed, setIsModifierKeyPressed] = useState(false);
  const [selectionRect, setSelectionRect] = useState<SelectionRect>();
  const [cardRects, setCardRects] = useState<CardRect[]>([]);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  const containerRef = useRef<T>(null);

  // Track modifier key status
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.shiftKey) {
        setIsModifierKeyPressed(true);
      }

      // Clear selection on Escape
      if (e.key === "Escape") {
        posthog.capture("cleared_drag_selection", {
          num_selected: selectedIds.length,
          was_dragging: isDraggingCards,
          method: "escape",
        });
        setSelectedIds([]);
        setIsDraggingCards(false);
        setDropTargetId(undefined);
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey || e.shiftKey)) {
        setIsModifierKeyPressed(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("keyup", handleKeyUp);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("keyup", handleKeyUp);
    };
  }, [selectedIds, isDraggingCards, posthog]);

  // Update card positions when grid changes
  useEffect(() => {
    if (!containerRef.current) return;

    const updateCardRects = () => {
      if (!containerRef.current) return;
      const containerRect = containerRef.current.getBoundingClientRect();

      const newRects = Array.from(
        containerRef.current.querySelectorAll<HTMLElement>("[data-card-id]"),
      ).map((card) => {
        const rect = card.getBoundingClientRect();
        return {
          id: card.getAttribute("data-card-id") || "",
          rect: {
            left: rect.left - containerRect.left,
            top: rect.top - containerRect.top,
            right: rect.right - containerRect.left,
            bottom: rect.bottom - containerRect.top,
          },
        };
      });

      setCardRects(newRects);
    };

    // Initial calculation
    updateCardRects();

    // Set up ResizeObserver
    const resizeObserver = new ResizeObserver(updateCardRects);
    resizeObserver.observe(containerRef.current);

    return () => resizeObserver.disconnect();
  }, []);

  // Track mouse position for selection box
  useEffect(() => {
    if (!selectionRect) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDraggingCards && selectionRect) {
        setMousePosition({ x: e.clientX, y: e.clientY });
      }
    };

    document.addEventListener("mousemove", handleMouseMove);
    return () => document.removeEventListener("mousemove", handleMouseMove);
  }, [isDraggingCards, selectionRect]);

  // Select cards in drag area
  const selectCardsInDragArea = useCallback(
    (newSelectionRect: SelectionRect, addToSelection = false) => {
      const left = Math.min(newSelectionRect.startX, newSelectionRect.currentX);
      const right = Math.max(
        newSelectionRect.startX,
        newSelectionRect.currentX,
      );
      const top = Math.min(newSelectionRect.startY, newSelectionRect.currentY);
      const bottom = Math.max(
        newSelectionRect.startY,
        newSelectionRect.currentY,
      );

      const intersectingIds = cardRects
        .filter(({ rect }) => {
          return !(
            rect.right < left ||
            rect.left > right ||
            rect.bottom < top ||
            rect.top > bottom
          );
        })
        .map(({ id }) => id);

      if (addToSelection) {
        setSelectedIds((prev) =>
          Array.from(new Set([...prev, ...intersectingIds])),
        );
      } else {
        setSelectedIds(intersectingIds);
      }

      if (intersectingIds.length) {
        setLastSelectedId(intersectingIds[0]);
      }
    },
    [cardRects],
  );

  // Handle selection box scroll
  useEffect(() => {
    if (!selectionRect) return;

    const handleScroll = () => {
      if (!containerRef.current) return;
      const containerRect = containerRef.current.getBoundingClientRect();

      const newSelectionRect = {
        ...(selectionRect as SelectionRect),
        currentX: mousePosition.x - containerRect.left,
        currentY: mousePosition.y - containerRect.top,
      };

      setSelectionRect(newSelectionRect);
      selectCardsInDragArea(newSelectionRect, isModifierKeyPressed);
    };

    document.addEventListener("scroll", handleScroll, true);
    return () => document.removeEventListener("scroll", handleScroll, true);
  }, [
    selectionRect,
    mousePosition,
    isModifierKeyPressed,
    selectCardsInDragArea,
  ]);

  // Mouse event handlers
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      // Don't start selection if we're still in dragging state
      if (isDraggingCards) return;

      // Ignore if clicking on a card
      if ((e.target as HTMLElement).closest("[data-card-id]")) return;

      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;

      // Clear selection when clicking empty space, unless Ctrl/Cmd is held
      if (!e.metaKey && !e.ctrlKey) {
        if (selectedIds.length > 0) {
          posthog.capture("cleared_drag_selection", {
            num_selected: selectedIds.length,
            was_dragging: isDraggingCards,
            method: "click",
          });
        }
        setSelectedIds([]);
      }

      setSelectionRect({
        startX: e.clientX - rect.left,
        startY: e.clientY - rect.top,
        currentX: e.clientX - rect.left,
        currentY: e.clientY - rect.top,
      });
    },
    [isDraggingCards, selectedIds, posthog],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      // Don't update selection if we're dragging
      if (isDraggingCards) return;

      if (selectionRect) {
        const rect = containerRef.current?.getBoundingClientRect();
        if (!rect) return;

        const newSelectionRect = {
          ...(selectionRect ?? {
            startX: e.clientX - rect.left,
            startY: e.clientY - rect.top,
          }),
          currentX: e.clientX - rect.left,
          currentY: e.clientY - rect.top,
        };
        setSelectionRect(newSelectionRect);

        selectCardsInDragArea(newSelectionRect, e.ctrlKey || e.metaKey);
      }
    },
    [isDraggingCards, selectionRect, selectCardsInDragArea],
  );

  const handleMouseUp = useCallback(() => {
    if (selectionRect) {
      posthog.capture("finish_drag_selection", {
        total_selected: selectedIds.length,
        drag_area: Math.abs(
          (selectionRect.currentX - selectionRect.startX) *
            (selectionRect.currentY - selectionRect.startY),
        ),
      });

      setSelectionRect(undefined);
    }
    // Reset drag state on mouse up
    setIsDraggingCards(false);
    setDropTargetId(undefined);
  }, [selectionRect, selectedIds.length, posthog]);

  // Card event handlers
  const handleSelect = useCallback(
    (id: string, e: React.MouseEvent) => {
      e.stopPropagation();

      if (e.ctrlKey || e.metaKey) {
        // Toggle selection for this card
        setSelectedIds((prev) =>
          prev.includes(id)
            ? prev.filter((selectedId) => selectedId !== id)
            : [...prev, id],
        );
      } else if (e.shiftKey && lastSelectedId) {
        // Select range between last selected and this card
        const allIds = cardRects.map((card) => card.id);
        const lastIndex = allIds.indexOf(lastSelectedId);
        const currentIndex = allIds.indexOf(id);

        if (lastIndex !== -1 && currentIndex !== -1) {
          const start = Math.min(lastIndex, currentIndex);
          const end = Math.max(lastIndex, currentIndex);
          const rangeIds = allIds.slice(start, end + 1);

          setSelectedIds((prev) => Array.from(new Set([...prev, ...rangeIds])));
        }
      } else {
        // Select only this card
        setSelectedIds([id]);
      }

      setLastSelectedId(id);
    },
    [lastSelectedId, cardRects],
  );

  const handleDragStart = useCallback(
    (id: string, e: React.DragEvent) => {
      e.stopPropagation();

      // Create an array of IDs to use for drag data
      let idsToUse: string[];

      // If item isn't already selected, use only this one
      if (!selectedIds.includes(id)) {
        setSelectedIds([id]);
        idsToUse = [id];
      } else {
        // Otherwise use all selected IDs
        idsToUse = selectedIds;
      }

      setIsDraggingCards(true);
      setLastSelectedId(id);

      // Set data for drop targets outside our component using the determined IDs
      const dragData = JSON.stringify(
        idsToUse.map((id) => ({ _id: id, isFolder: true })),
      );
      e.dataTransfer.setData("application/json", dragData);

      e.dataTransfer.effectAllowed = "move";
    },
    [selectedIds],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  }, []);

  const handleDragEnter = useCallback(
    (id: string, e: React.DragEvent) => {
      e.preventDefault();

      // Don't set drop target if we're dragging over one of the selected cards
      if (!selectedIds.includes(id)) {
        setDropTargetId(id);
      }
    },
    [selectedIds],
  );

  const handleDragLeave = useCallback(
    (id: string, e: React.DragEvent) => {
      e.preventDefault();

      if (dropTargetId === id) {
        setDropTargetId(undefined);
      }
    },
    [dropTargetId],
  );

  const handleDrop = useCallback(
    (id: string, e: { preventDefault: () => void }) => {
      e.preventDefault();
      setDropTargetId(undefined);
      setIsDraggingCards(false);
      setSelectedIds([]);
    },
    [],
  );

  return {
    containerRef,
    state: {
      selectedIds,
      isDraggingCards,
      dropTargetId,
      lastSelectedId,
      isModifierKeyPressed,
      selectionRect,
    },
    actions: {
      setSelectedIds,
      setIsDraggingCards,
      setDropTargetId,
      setLastSelectedId,
    },
    handlers: {
      handleMouseDown,
      handleMouseMove,
      handleMouseUp,
      handleSelect,
      handleDragStart,
      handleDragOver,
      handleDragEnter,
      handleDragLeave,
      handleDrop,
    },
  };
}
